import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDeleteSupplierById, useGetSuppliers } from "../../../../api/supplier";
import { useGetCountriesData } from "../../../../api/static-data";
import { errorView } from "../../../../helpers/ErrorHandler";
import { SupplierListFilters, SupplierListViewModel, SupplierRow } from "../types/supplierListView.types";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Owns every piece of state/data-fetching the legacy `SupplierList` kept
 * inline (see src/screens/supplier/supplierList/SupplierList.tsx) — same
 * search debounce, same server-side pagination, same filter shape, same
 * delete guard rules (`ingredients.length`, `is_supplier_as_user`). Ported
 * logic-for-logic; the only behavioral difference is presentational
 * (see `useSupplierFilterState`/`SupplierListPagination`).
 */
export const useSupplierListViewModel = (): SupplierListViewModel => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchKeyInput, setSearchKeyInput] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [filters, setFilters] = useState<SupplierListFilters>({
    is_supplier_as_user: undefined,
    supplier_type: [],
    orderBy: "createdAtDesc"
  });

  const [supplierEdit, setSupplierEdit] = useState<SupplierRow | null>(null);
  const [supplierDetails, setSupplierDetails] = useState<SupplierRow | null>(null);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [deleteData, setDeleteData] = useState<SupplierRow | null>(null);

  const { data: countriesData, isLoading: isCountriesLoading } = useGetCountriesData();
  const countryNameById = useMemo(() => {
    const map = new Map<number, string>();
    (countriesData || []).forEach((c: any) => map.set(c.key, c.text));
    return map;
  }, [countriesData]);

  const { data: supplierData, isLoading: isRowsLoading, refetch } = useGetSuppliers({
    pageNumber,
    pageSize,
    searchKey,
    ...filters
  });

  const { mutate: deleteSupplier } = useDeleteSupplierById();

  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchKey(value), 500),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchKeyInput(value);
    debouncedSearch(value);
  };

  const refetchWithFilters = (
    isSupplierAsUser: boolean | undefined,
    supplierTypes: number[],
    orderBy: string
  ) => {
    setFilters({ is_supplier_as_user: isSupplierAsUser, supplier_type: supplierTypes, orderBy });
  };

  const openDetailsModal = (row: SupplierRow) => setSupplierDetails(row);
  const closeDetailsModal = () => setSupplierDetails(null);
  const navigateDetailsToEdit = () => {
    setSupplierEdit(supplierDetails);
    setSupplierDetails(null);
  };

  const openEditModal = (row: SupplierRow) => setSupplierEdit(row);
  const closeEditModal = () => setSupplierEdit(null);
  const onSuccessEdit = () => {
    setSupplierEdit(null);
    refetch();
  };

  const openDeleteConfirm = (row: SupplierRow) => {
    setDeleteData(row);
    setVisibleDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };

  const confirmDelete = () => {
    if (deleteData?.ingredients?.length) {
      errorView("This Supplier has Ingredients");
      setVisibleDeleteConfirm(false);
      return;
    }
    if (deleteData?.is_supplier_as_user) {
      errorView("This Supplier registered as a user");
      setVisibleDeleteConfirm(false);
      return;
    }
    if (deleteData?._id) {
      deleteSupplier(deleteData._id, { onSuccess: () => refetch() });
    }
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };

  return {
    rows: supplierData?.result || [],
    isRowsLoading,
    isCountriesLoading,
    countryNameById,
    totalCount: supplierData?.total || 0,
    totalPages: Math.ceil((supplierData?.total || 0) / (supplierData?.pageSize || pageSize)) || 1,
    pageNumber,
    searchKey: searchKeyInput,

    setSearchKey: handleSearchChange,
    handlePagination: setPageNumber,
    refetchWithFilters,

    supplierEdit,
    supplierDetails,
    openEditModal,
    closeEditModal,
    onSuccessEdit,
    openDetailsModal,
    closeDetailsModal,
    navigateDetailsToEdit,

    visibleDeleteConfirm,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete
  };
};
